import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Modal from 'react-modal';
import { InteractionType } from '@azure/msal-browser';
import {
  AuthenticatedTemplate,
  MsalAuthenticationTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  LanguageProvider,
  LoadingSpinner,
} from '@data-products-and-ai/react-components';
import { ErrorBoundary } from './contexts/ErrorBoundary';
import { CategoryProvider } from './contexts/TagCategories/TagCategories';
import { CONSTANTS } from './constants/constants';
import { Home } from './pages/Home';
import { Layout } from './pages/Layout';
import './App.scss';

const DataQuality = lazy(() => import('@/pages/DataQuality/DataQuality'));
const Login = lazy(() => import('@/pages/Login/Login'));
const About = lazy(() => import('@/pages/About/About'));
const Frames = lazy(() => import('@/pages/Frames/Frames'));
const Frame = lazy(() => import('@/pages/Frame/Frame'));
const Parts = lazy(() => import('@/pages/Parts/Parts'));
const Part = lazy(() => import('@/pages/Part/Part'));
const PartCompare = lazy(() => import('@/pages/PartCompare/PartCompare'));
const PartPedigree = lazy(() => import('@/pages/PartPedigree/PartPedigree'));
const Administration = lazy(
  () => import('@/pages/Administration/Administration'),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 20 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: 2,
      retryDelay: 3000,
    },
  },
});

const NotFoundPage = () => {
  return <>Not found</>;
};

const App = () => {
  Modal.setAppElement('#root');

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LanguageProvider translations={CONSTANTS} defaultLanguage="en">
          <QueryClientProvider client={queryClient}>
            <MsalAuthenticationTemplate
              interactionType={InteractionType.Redirect}
            >
              <AuthenticatedTemplate>
                <CategoryProvider>
                  <Suspense
                    fallback={
                      <div
                        style={{
                          width: '100vw',
                          height: '100vh',
                          alignContent: 'center',
                        }}
                      >
                        <LoadingSpinner />
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/" element={<Layout />}>
                        <Route index element={<Home />} />
                        <Route path="administration">
                          <Route index element={<Administration />} />
                          <Route path=":status" element={<Administration />} />
                        </Route>
                        <Route path="about" element={<About />} />
                        <Route
                          path="data"
                          element={<Parts searchType="data_approval" />}
                        />
                        <Route path="parts">
                          <Route index element={<Parts />} />
                          <Route path="search/:search_id" element={<Parts />} />
                          <Route path="detail/:part_id" element={<Part />} />
                          <Route path="compare" element={<PartCompare />} />
                          <Route
                            path="compare/:part_id"
                            element={<PartCompare />}
                          />
                          <Route
                            path="part_development"
                            element={<PartPedigree />}
                          />
                          <Route
                            path="part_development/:part_id"
                            element={<PartPedigree />}
                          />
                        </Route>
                        <Route path="frames">
                          <Route index element={<Frames />} />
                          <Route path=":frame_family_id" element={<Frame />} />
                          <Route
                            path=":frame_family_id/:frame_id"
                            element={<Frame />}
                          />
                        </Route>
                        <Route path="data_quality">
                          <Route index element={<DataQuality />} />
                        </Route>
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </CategoryProvider>
              </AuthenticatedTemplate>
              <UnauthenticatedTemplate>
                <Login />
              </UnauthenticatedTemplate>
            </MsalAuthenticationTemplate>
          </QueryClientProvider>
        </LanguageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
